'use client';

import {
  ModButton,
  ModModal,
  ModModalContent,
  ModModalFooter
} from '@moderneinc/design-system-components';
import {
  borderRadius,
  semanticColors,
  typography
} from '@moderneinc/design-system-tokens';
import { alpha, Box, ButtonBase, Stack, Typography } from '@mui/material';
import type { FunctionComponent, ReactNode } from 'react';
import { useTourEngineStore } from '../../stores/tour-engine.store';
import { Link } from '../utilities/moderne-link/moderne-link.component';
import { DAY_ZERO_TOUR_ID, dayZeroTourConfig } from './day-zero-tour.config';

type PaletteColor = 'primary' | 'success' | 'warning';

// Icons are inlined (lucide path data) rather than imported from the icon
// barrel: the barrel runs createSvgIcon at module load, which breaks under the
// vitest vmThreads transform, and this modal is rendered by the app-wide tour
// provider (so the barrel import would break the provider's tests too).
const Glyph: FunctionComponent<{ children: ReactNode }> = ({ children }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

type NextStep = {
  label: string;
  description: string;
  href: string;
  icon: ReactNode;
  color: PaletteColor;
};

const NEXT_STEPS: NextStep[] = [
  {
    label: 'Visualizations',
    description: 'See interactive charts and graphs of your recipe results',
    href: 'https://docs.moderne.io/user-documentation/moderne-platform/getting-started/visualizations',
    icon: (
      <Glyph>
        <path d="M3 3v16a2 2 0 0 0 2 2h16" />
        <path d="M18 17V9" />
        <path d="M13 17V5" />
        <path d="M8 17v-3" />
      </Glyph>
    ),
    color: 'primary'
  },
  {
    label: 'DevCenter',
    description:
      'Your dashboard for tracking upgrades, migrations, and security fixes',
    href: 'https://docs.moderne.io/user-documentation/moderne-platform/getting-started/dev-center',
    icon: (
      <Glyph>
        <path d="M21 12c.552 0 1.005-.449.95-.998a10 10 0 0 0-8.953-8.951c-.55-.055-.998.398-.998.95v8a1 1 0 0 0 1 1z" />
        <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
      </Glyph>
    ),
    color: 'success'
  },
  {
    label: 'Builder',
    description: 'Create custom recipes by combining existing ones',
    href: 'https://docs.moderne.io/user-documentation/moderne-platform/how-to-guides/new-recipe-builder',
    icon: (
      <Glyph>
        <path d="M10 22V7a1 1 0 0 0-1-1H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5a1 1 0 0 0-1-1H2" />
        <rect x="14" y="2" width="8" height="8" rx="1" />
      </Glyph>
    ),
    color: 'warning'
  }
];

/** One dot per tour phase, all filled to signal completion. Driven by the
    config's phases so the count can't drift from the actual tour. */
const PHASE_DOT_KEYS = dayZeroTourConfig.highLevelSteps.map(
  (phase) => phase.label
);

export const TourCompletionModal: FunctionComponent = () => {
  const status = useTourEngineStore((state) => state.status);
  const activeTourId = useTourEngineStore((state) => state.activeTourId);
  const resume = useTourEngineStore((state) => state.resume);
  const dismiss = useTourEngineStore((state) => state.dismiss);

  // `open` is derived straight from store status: when the tour leaves
  // 'completed' (a restart), the modal closes, and a later completion re-opens
  // it. No local dismissed flag that can get stuck across runs.
  const open = status === 'completed' && activeTourId === DAY_ZERO_TOUR_ID;

  // "Done" ends the tour (closing the modal); "Restart tour" relaunches it.
  const handleClose = () => {
    dismiss();
  };

  const handleRestart = () => {
    resume(DAY_ZERO_TOUR_ID);
  };

  return (
    <ModModal open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <ModModalContent>
        <Typography
          sx={{
            fontFamily: typography.fontFamily.heading,
            fontWeight: typography.fontWeight.bold,
            fontSize: typography.fontSize.h3,
            // No ModModalHeader above, so add top padding to the title so it
            // doesn't sit flush against the modal's top edge.
            pt: 2
          }}
        >
          You&apos;re all set!
        </Typography>
        <Typography
          variant="body"
          sx={{ color: semanticColors.color.text.secondary, mt: 1 }}
        >
          You&apos;ve completed the getting started tour! When you&apos;re
          ready, you can <strong>commit your changes</strong> to create pull
          requests using the Commit button below. Here are some more things to
          explore:
        </Typography>

        <Stack sx={{ gap: 1.5, mt: 2.5 }}>
          {NEXT_STEPS.map((step) => (
            <Link
              key={step.label}
              href={step.href}
              external
              sx={{
                display: 'flex',
                gap: 1.5,
                alignItems: 'flex-start',
                p: 2,
                borderRadius: `${borderRadius.m}px`,
                border: `1px solid ${semanticColors.color.border.primary}`,
                color: 'text.primary',
                textDecoration: 'none',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  textDecoration: 'none'
                }
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  width: 40,
                  height: 40,
                  borderRadius: `${borderRadius.s}px`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: (theme) =>
                    alpha(theme.palette[step.color].main, 0.12),
                  color: (theme) => theme.palette[step.color].main
                }}
              >
                {step.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: typography.fontWeight.bold }}>
                  {step.label}
                </Typography>
                <Typography
                  variant="body"
                  sx={{ color: semanticColors.color.text.secondary }}
                >
                  {step.description}
                </Typography>
              </Box>
            </Link>
          ))}
        </Stack>
      </ModModalContent>

      <ModModalFooter
        leadingContent={
          <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center' }}>
            <ModButton variant="primary" size="medium" onClick={handleClose}>
              Done
            </ModButton>
            <ButtonBase
              onClick={handleRestart}
              sx={{ borderRadius: `${borderRadius.s}px`, px: 1, py: 0.5 }}
            >
              <Typography
                variant="bodySm"
                sx={{ color: semanticColors.color.text.secondary }}
              >
                Restart tour
              </Typography>
            </ButtonBase>
          </Stack>
        }
      >
        <Stack
          direction="row"
          aria-hidden="true"
          sx={{ gap: 0.75, alignItems: 'center' }}
        >
          {PHASE_DOT_KEYS.map((key) => (
            <Box
              key={key}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '999px',
                backgroundColor: 'primary.main'
              }}
            />
          ))}
        </Stack>
      </ModModalFooter>
    </ModModal>
  );
};
